/**
 * MasterDataListWarehouseFormDialog — Create/Edit `list_warehouse`.
 * 1:1 port of Access `frmMdQryListWarehouseAdd`. Compact Sales Contract style.
 *
 * Single-column (Access all inputs Left=3024), field order by Top:
 *   Warehouse Index (editable số) → Warehouse ID (readonly) → Warehouse Name →
 *   WH Sampling Name → Address.
 * Access: warehouse_code (PK) = "WH" & Format(warehouse_index,"00") — user nhập
 * INDEX số, code derive readonly. New: Suggest_IndexName("list_warehouse") →
 * index + code. Edit: index disable, code readonly. VBA errCheck: index trống →
 * "Please enter Warehouse Index!"; code trùng (create) → "Warehouse ID is existed!".
 * warehouse_index KHÔNG lưu (form-only); payload gửi warehouse_code (PK).
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdListWarehouseMutation,
  useLazyCheckMdWarehouseCodeQuery,
  useLazySuggestMdWarehouseQuery,
  useUpdateMdListWarehouseMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataListWarehouse } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  roSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataListWarehouse | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  warehouse_index: string
  warehouse_code: string
  warehouse: string
  wh_sampling_name: string
  address: string
}

const EMPTY: FormState = { warehouse_index: '', warehouse_code: '', warehouse: '', wh_sampling_name: '', address: '' }

// Access: warehouse_code = "WH" & Format(index,"00"). Chỉ derive khi index là số.
const codeFromIndex = (idx: string): string => {
  const t = idx.trim()
  if (t === '' || !/^\d+$/.test(t)) return ''
  return `WH${t.padStart(2, '0')}`
}

export default function MasterDataListWarehouseFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createWarehouse, { isLoading: creating }] = useCreateMdListWarehouseMutation()
  const [updateWarehouse, { isLoading: updating }] = useUpdateMdListWarehouseMutation()
  const [triggerSuggest, suggestState] = useLazySuggestMdWarehouseQuery()
  const [triggerCheck] = useLazyCheckMdWarehouseCodeQuery()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (isEdit && initialValue) {
      const code = initialValue.warehouse_code ?? ''
      setForm({
        warehouse_index: code.replace(/^WH/i, ''),
        warehouse_code: code,
        warehouse: initialValue.warehouse ?? '',
        wh_sampling_name: initialValue.wh_sampling_name ?? '',
        address: initialValue.address ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  // New: auto-suggest index + code khi mở (guard chỉ set khi trống).
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggest(undefined, false).unwrap().then((res) => {
      if (cancelled) return
      const idx = String(res.index ?? '').trim()
      if (!idx) return
      setForm((prev) => (prev.warehouse_index.trim() ? prev : {
        ...prev,
        warehouse_index: idx,
        warehouse_code: res.warehouse_code || codeFromIndex(idx),
      }))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, isEdit, triggerSuggest])

  useEffect(() => {
    if (regenNotice && form.warehouse_code !== regenNotice.to) setRegenNotice(null)
  }, [form.warehouse_code, regenNotice])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  // Index đổi → derive lại warehouse_code (readonly).
  const handleIndexChange = (v: string) => {
    setForm((p) => ({ ...p, warehouse_index: v, warehouse_code: codeFromIndex(v) }))
  }

  const indexEmpty = !form.warehouse_index.trim()
  const codeEmpty = !form.warehouse_code.trim()
  const canSave = !indexEmpty && !codeEmpty && !loading

  // Blur index → check derived code trùng, nếu có thì suggest index/code mới.
  const handleIndexBlur = async () => {
    if (isEdit) return
    const code = form.warehouse_code.trim()
    if (!code) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck(code, false).unwrap()
      if (!check.exists) return
      const next = await triggerSuggest(undefined, false).unwrap()
      const idx = String(next.index ?? '').trim()
      const newCode = next.warehouse_code || codeFromIndex(idx)
      if (newCode && newCode !== code) {
        setForm((prev) => ({ ...prev, warehouse_index: idx, warehouse_code: newCode }))
        setRegenNotice({ from: code, to: newCode })
      }
    } catch {
      /* BE raise lúc Save nếu thật sự trùng */
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    warehouse: form.warehouse.trim() || null,
    wh_sampling_name: form.wh_sampling_name.trim() || null,
    address: form.address.trim() || null,
  })

  const handleSave = async () => {
    if (precheckBusyRef.current) return
    if (indexEmpty || codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateWarehouse({ code: initialValue.warehouse_code, body: buildBody() }).unwrap()
        snack.success('Warehouse updated')
      } else {
        await createWarehouse({ warehouse_code: form.warehouse_code.trim(), ...buildBody() }).unwrap()
        snack.success('Warehouse created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      const lower = msg.toLowerCase()
      if (!isEdit && (lower.includes('exist') || lower.includes('duplicate'))) {
        try {
          const next = await triggerSuggest(undefined, false).unwrap()
          const idx = String(next.index ?? '').trim()
          const newCode = next.warehouse_code || codeFromIndex(idx)
          const oldCode = form.warehouse_code.trim()
          if (newCode && newCode !== oldCode) {
            setForm((prev) => ({ ...prev, warehouse_index: idx, warehouse_code: newCode }))
            setRegenNotice({ from: oldCode, to: newCode })
            return
          }
        } catch {
          /* fallthrough */
        }
      }
      snack.error(msg)
    }
  }

  const indexHelper = isEdit
    ? 'Warehouse ID cannot be changed after creation.'
    : precheckBusy
      ? 'Verifying ID…'
      : suggestState.isFetching
        ? 'Suggesting next index…'
        : 'Warehouse ID auto-derives as WH + index.'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit List Warehouse' : 'New List Warehouse'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.warehouse_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button type="button" variant="contained" size="small" color="success" onClick={() => void handleSave()} disabled={!canSave} sx={dialogHeaderBtnSx}>
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Warehouse ID <b>{regenNotice.from}</b> was just created — suggested new ID <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Warehouse Index"
              size="small"
              fullWidth
              required
              type="number"
              disabled={isEdit}
              error={showErrors && indexEmpty}
              value={form.warehouse_index}
              onChange={(e) => handleIndexChange(e.target.value)}
              onBlur={() => void handleIndexBlur()}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1, inputMode: 'numeric', step: 1 }}
              helperText={indexHelper}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Warehouse ID"
              size="small"
              fullWidth
              value={form.warehouse_code}
              error={showErrors && codeEmpty}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              inputProps={{ tabIndex: -1 }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Warehouse Name"
              size="small"
              fullWidth
              value={form.warehouse}
              onChange={(e) => set('warehouse', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Warehouse Sampling Name"
              size="small"
              fullWidth
              value={form.wh_sampling_name}
              onChange={(e) => set('wh_sampling_name', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              size="small"
              fullWidth
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
